import React, { memo, useState, useEffect } from 'react';
import client from './api/client';
import PageHeader from './components/PageHeader';
import { formatPrice } from './utils/formatPrice';
import './PriceAnalysisPage.css';

const volumeFormatter = new Intl.NumberFormat();
const CACHE_KEY = 'price_analysis_data_v5';
const MA_CACHE_KEY = 'price_analysis_ma_v4';
const TOP_N = 50;
const PERIODS = [7, 30, 90, 180, 365];
const defaultMinHistory = (p) => Math.max(3, Math.floor(p / 4));

const DeviationTable = memo(function DeviationTable({ title, rows, sign, period, sortMetric, onSortChange }) {
  return (
    <div className="pa-section">
      <h2 className={`pa-section-title ${sign}`}>{title}</h2>
      <div className="pa-table-wrapper">
        <table className="data-table pa-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Item</th>
              <th className="num">Current Price</th>
              <th className="num">Avg Price</th>
              <th
                className={`num pa-th-sortable${sortMetric === 'avg' ? ' pa-th-active' : ''}`}
                onClick={() => onSortChange('avg')}
              >
                Δ vs Avg {sortMetric === 'avg' ? '▼' : ''}
              </th>
              <th
                className={`num pa-th-sortable${sortMetric === 'point' ? ' pa-th-active' : ''}`}
                onClick={() => onSortChange('point')}
              >
                Δ vs {period}d ago {sortMetric === 'point' ? '▼' : ''}
              </th>
              <th className="num">Avg Daily Vol</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.type_id}>
                <td className="pa-rank">{i + 1}</td>
                <td className="pa-name">{row.name}</td>
                <td className="pa-price">{formatPrice(row.current_price)}</td>
                <td className="pa-price">{formatPrice(row.avg_price)}</td>
                <td className={`pa-deviation ${row.deviation_pct >= 0 ? 'positive' : 'negative'}`}>
                  {row.deviation_pct >= 0 ? '+' : ''}{row.deviation_pct.toFixed(1)}%
                </td>
                <td className={
                  row.deviation_vs_point != null
                    ? `pa-deviation ${row.deviation_vs_point >= 0 ? 'positive' : 'negative'}`
                    : 'pa-deviation pa-deviation-null'
                }>
                  {row.deviation_vs_point != null
                    ? `${row.deviation_vs_point >= 0 ? '+' : ''}${row.deviation_vs_point.toFixed(1)}%`
                    : '—'}
                </td>
                <td className="pa-volume">
                  {volumeFormatter.format(Math.round(row.avg_daily_volume))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

const MACrossoverTable = memo(function MACrossoverTable({ rows }) {
  return (
    <div className="pa-ma-section">
      <h2 className="pa-section-title pa-ma-title">MA Crossover Signals</h2>
      <p className="pa-ma-subtitle">
        5d MA just crossed above 95% of 20d MA — fresh signals only (within last 2 days)
      </p>
      <div className="pa-table-wrapper">
        <table className="data-table pa-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Item</th>
              <th className="num">Current Price</th>
              <th className="num">5d MA</th>
              <th className="num">20d MA</th>
              <th className="num">MA Gap</th>
              <th className="num">Price vs 5d MA</th>
              <th>Crossed</th>
              <th className="num">Avg Daily Vol</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.type_id}>
                <td className="pa-rank">{i + 1}</td>
                <td className="pa-name">{row.name}</td>
                <td className="pa-price">{formatPrice(row.current_price)}</td>
                <td className="pa-price">{formatPrice(row.today_5d_ma)}</td>
                <td className="pa-price">{formatPrice(row.today_20d_ma)}</td>
                <td className={`pa-deviation ${row.ma_gap_pct >= 0 ? 'positive' : 'negative'}`}>
                  {row.ma_gap_pct >= 0 ? '+' : ''}{row.ma_gap_pct.toFixed(2)}%
                </td>
                <td className={`pa-deviation ${row.price_vs_5d_ma_pct <= 0 ? 'positive' : 'negative'}`}>
                  {row.price_vs_5d_ma_pct >= 0 ? '+' : ''}{row.price_vs_5d_ma_pct.toFixed(2)}%
                </td>
                <td className="pa-crossed">
                  {row.days_since_crossover === 0 ? 'Today' : 'Yesterday'}
                </td>
                <td className="pa-volume">
                  {volumeFormatter.format(row.avg_daily_volume)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

function PriceAnalysisPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(365);
  const [minHistoryDays, setMinHistoryDays] = useState(defaultMinHistory(365));
  const [minDailyValueB, setMinDailyValueB] = useState(5);
  const [minOrders, setMinOrders] = useState(5);
  const [sortMetric, setSortMetric] = useState('avg'); // 'avg' | 'point'

  const [maData, setMaData] = useState(null);
  const [maLoading, setMaLoading] = useState(true);
  const [maError, setMaError] = useState(null);

  useEffect(() => {
    const load = async () => {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          setData(JSON.parse(cached));
          setLoading(false);
        } catch {}
      }
      try {
        const res = await client.get('/eve/market/price-deviations/');
        setData(res.data);
        setError(null);
        // Clean up old cache versions before writing new data to avoid quota errors
        ['v1', 'v2', 'v3', 'v4'].forEach(v => localStorage.removeItem(`price_analysis_data_${v}`));
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(res.data)); } catch {}
      } catch (err) {
        if (!cached) setError('Failed to load price analysis. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      const cached = localStorage.getItem(MA_CACHE_KEY);
      if (cached) {
        try {
          setMaData(JSON.parse(cached));
          setMaLoading(false);
        } catch {}
      }
      try {
        const res = await client.get('/eve/market/ma-crossovers/');
        setMaData(res.data);
        setMaError(null);
        localStorage.removeItem('price_analysis_ma_v2');
        localStorage.removeItem('price_analysis_ma_v3');
        try { localStorage.setItem(MA_CACHE_KEY, JSON.stringify(res.data)); } catch {}
      } catch (err) {
        if (!cached) setMaError('Failed to load MA crossover signals.');
      } finally {
        setMaLoading(false);
      }
    };
    load();
  }, []);

  // Reset min history days to a sensible default whenever the period changes
  useEffect(() => {
    setMinHistoryDays(defaultMinHistory(period));
  }, [period]);

  const computeRows = () => {
    if (!data?.items) return { gainers: [], losers: [] };
    const p = String(period);

    const processed = data.items
      .filter(item => {
        const pd = item.periods?.[p];
        return (
          pd &&
          pd.data_points >= minHistoryDays &&
          pd.avg_price > 0 &&
          item.current_price &&
          (pd.avg_daily_value ?? 0) >= minDailyValueB * 1e9 &&
          (pd.avg_order_count ?? 0) >= minOrders
        );
      })
      .map(item => {
        const pd = item.periods[p];
        const deviation_pct = Math.round((item.current_price - pd.avg_price) / pd.avg_price * 10000) / 100;
        const deviation_vs_point = pd.point_price != null
          ? Math.round((item.current_price - pd.point_price) / pd.point_price * 10000) / 100
          : null;
        return {
          type_id: item.type_id,
          name: item.name,
          current_price: item.current_price,
          avg_price: pd.avg_price,
          deviation_pct,
          deviation_vs_point,
          avg_daily_volume: pd.avg_daily_volume,
        };
      });

    const sortKey = sortMetric === 'point' ? 'deviation_vs_point' : 'deviation_pct';
    const eligible = sortMetric === 'point'
      ? processed.filter(r => r.deviation_vs_point != null)
      : processed;

    eligible.sort((a, b) => b[sortKey] - a[sortKey]);

    const gainers = eligible.filter(r => r[sortKey] > 0).slice(0, TOP_N);
    const losers = eligible.filter(r => r[sortKey] < 0).reverse().slice(0, TOP_N);
    return { gainers, losers };
  };

  const computeCrossoverRows = () => {
    if (!maData?.items) return [];
    return maData.items.filter(item =>
      item.avg_daily_value >= minDailyValueB * 1e9 &&
      item.avg_order_count >= minOrders
    );
  };

  const { gainers, losers } = computeRows();
  const crossoverRows = computeCrossoverRows();

  const gainerTitle = sortMetric === 'point'
    ? `Top Gainers (up from ${period}d ago)`
    : `Top Gainers (trading above ${period}d average)`;
  const loserTitle = sortMetric === 'point'
    ? `Top Losers (down from ${period}d ago)`
    : `Top Losers (trading below ${period}d average)`;

  return (
    <div className="page pa-container">
      <PageHeader
        title="Price Analysis"
        meta={`Items trading furthest from their ${period}-day Jita ${sortMetric === 'point' ? 'point-in-time price' : 'average'}`}
      />
      <div className="pa-content">

        <div className="pa-filters">
          <label className="pa-filter-label">
            Period
            <span className="seg">
              {PERIODS.map(p => (
                <button
                  key={p}
                  className={`seg-btn${period === p ? ' active' : ''}`}
                  onClick={() => setPeriod(p)}
                >
                  {p}d
                </button>
              ))}
            </span>
          </label>

          <label className="pa-filter-label">
            Min history days
            <span className="pa-filter-input-wrap">
              <input
                type="number"
                className="pa-filter-input"
                value={minHistoryDays}
                min={1}
                max={period}
                step={1}
                onChange={e => setMinHistoryDays(Math.max(1, Math.min(period, Number(e.target.value))))}
              />
            </span>
          </label>

          <label className="pa-filter-label">
            Min daily value
            <span className="pa-filter-input-wrap">
              <input
                type="number"
                className="pa-filter-input"
                value={minDailyValueB}
                min={0}
                step={1}
                onChange={e => setMinDailyValueB(Math.max(0, Number(e.target.value)))}
              />
              <span className="pa-filter-unit">B ISK</span>
            </span>
          </label>

          <label className="pa-filter-label">
            Min orders/day
            <span className="pa-filter-input-wrap">
              <input
                type="number"
                className="pa-filter-input"
                value={minOrders}
                min={0}
                step={1}
                onChange={e => setMinOrders(Math.max(0, Number(e.target.value)))}
              />
            </span>
          </label>
        </div>

        {loading && <p className="pa-loading">Loading...</p>}
        {error && <p className="pa-error">{error}</p>}

        {data && (
          <>
            <div className="pa-meta">
              {(data.meta?.total_items ?? 0).toLocaleString()} items loaded &middot;{' '}
              {gainers.length + losers.length} shown after filters
            </div>
            <div className="pa-grid">
              <DeviationTable
                title={gainerTitle}
                rows={gainers}
                sign="positive"
                period={period}
                sortMetric={sortMetric}
                onSortChange={setSortMetric}
              />
              <DeviationTable
                title={loserTitle}
                rows={losers}
                sign="negative"
                period={period}
                sortMetric={sortMetric}
                onSortChange={setSortMetric}
              />
            </div>
          </>
        )}

        <div className="pa-ma-container">
          {maLoading && <p className="pa-loading">Loading MA signals...</p>}
          {maError && <p className="pa-error">{maError}</p>}
          {maData && (
            crossoverRows.length > 0
              ? <MACrossoverTable rows={crossoverRows} />
              : <p className="pa-ma-empty">No MA crossover signals after filters.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default PriceAnalysisPage;
